<template>
  <el-date-picker :value="value"
                  :type="type"
                  :size="size"
                  :format="format"
                  :value-format="realValueFormat"
                  :readonly="readonly"
                  :placeholder="placeholder || type.includes('range') ? undefined : '请选择'"
                  :startPlaceholder="startPlaceholder || Array.isArray(DATE_PLACEHOLDER_BY_TYPE[type]) ? DATE_PLACEHOLDER_BY_TYPE[type][0] : ''"
                  :endPlaceholder="endPlaceholder || Array.isArray(DATE_PLACEHOLDER_BY_TYPE[type]) ? DATE_PLACEHOLDER_BY_TYPE[type][1] : ''"
                  :prefixIcon="prefixIcon"
                  :clearIcon="clearIcon"
                  :name="name"
                  :disabled="typeof disabled === 'boolean' ? disabled : false"
                  :clearable="clearable"
                  :id="id"
                  :editable="editable"
                  :align="align"
                  :default-value="defaultValue"
                  :default-time="defaultTime"
                  :range-separator="rangeSeparator"
                  :picker-options="getPickerOptions"
                  :unlink-panels="unlinkPanels"
                  :validate-event="validateEvent"
                  ref="datePicker"
                  @input="onInput"
                  @focus="onFocus"
                  v-on="$listeners"/>
</template>

<script>
import { parseDate, formatDate, isDateObject, modifyWithTimeString } from "element-ui/lib/utils/date-util"
import { DATE_FORMAT_BY_TYPE, DATE_PLACEHOLDER_BY_TYPE, getDisabledTime, getPickerOptions, setDisabledTimeSelect } from "./util"
export default {
    name: "ZjDatePicker",
    props: {
        type: {
            type: String,
            default: "date"
        },
        // 当前时间往后或者往前延长多少取决于disabled
        currentDateAddMinutes: {
            type: Number,
            default: 0
        },
        size: String,
        format: String,
        valueFormat: {
            type: String
        },
        readonly: {
            type: Boolean,
            default: false
        },
        placeholder: String,
        startPlaceholder: String,
        endPlaceholder: String,
        prefixIcon: String,
        clearIcon: {
            type: String,
            default: "el-icon-circle-close"
        },
        name: {
            default: "",
            type: String
        },
        disabled: {
            type: [Boolean, String],
            validator (val) {
                return [true, false, undefined, "before", "after"].includes(val)
            }
        },
        clearable: {
            type: Boolean,
            default: true
        },
        id: {
            default: ""
        },
        editable: {
            type: Boolean,
            default: false
        },
        align: {
            type: String,
            default: "left"
        },
        value: {},
        defaultValue: {},
        defaultTime: {
            type: [Array, String]
        },
        rangeSeparator: {
            default: "至"
        },
        pickerOptions: {},
        unlinkPanels: {
            type: Boolean
        },
        validateEvent: {
            type: Boolean,
            default: true
        }
    },
    data () {
        return {
            dynamicDefaultTime: [],
            DATE_PLACEHOLDER_BY_TYPE,
            DATE_FORMAT_BY_TYPE
        }
    },
    computed: {
        realValueFormat ({ valueFormat, type }) {
            return valueFormat || DATE_FORMAT_BY_TYPE[type]
        },
        // 获取时间快捷键
        getPickerOptions () {
            const { disabled, type, pickerOptions = Object.create(null) } = this
            if (disabled === true || (/^(month)|(week)/.test(type))) return undefined
            const isRange = type.includes("range")
            const pickOptions = getPickerOptions.bind(this)
            if (!disabled) {
                const getShortcuts = (disabled, startIndex = 0, endIndex = -4) => pickOptions(isRange, disabled).shortcuts.slice(startIndex, endIndex)
                const shortcuts = [...getShortcuts("after"), ...getShortcuts("before", 1)]
                return Object.assign({ shortcuts }, pickerOptions)
            }
            return Object.assign(pickOptions(isRange, disabled), pickerOptions)
        },
    },
    methods: {
        onInput (val) {
            this.$emit("input", val)
        },
        onFocus (e) {
            if (this.type.includes("time") && ["before", "after"].includes(this.disabled)) {
                const datePicker = this.getDatePickerInstance()
                datePicker.$nextTick().then(() => {
                    const picker = datePicker.picker
                    if (this.type === "datetimerange") {
                        this.formatDateTimeRange(picker)
                    } else {
                        this.formatDateTime(picker)
                    }
                })
            }
            this.$emit("focus", e)
        },
        // 获取datepicker实例
        getDatePickerInstance () {
            return this.$refs.datePicker
        },
        formatDateTimeRange (picker) {
            let isUpdate = true
            const callback = (val) => {
                if (picker.maxVisibleDate || picker.minVisibleDate) {
                    const currentDate = new Date()
                    if (formatDate(currentDate) === formatDate(val)) {
                        if (isUpdate) {
                            setDisabledTimeSelect.call(picker, currentDate, this.disabled)
                            isUpdate = false
                        }
                    } else {
                        const maxTimeSelectableRange = picker.$refs.maxTimePicker.selectableRange
                        const minTimeSelectableRange = picker.$refs.minTimePicker.selectableRange
                        if (Array.isArray(maxTimeSelectableRange) && maxTimeSelectableRange.length !== 0) {
                            picker.$refs.maxTimePicker.selectableRange = []
                            isUpdate = true
                        }
                        if (Array.isArray(minTimeSelectableRange) && minTimeSelectableRange.length !== 0) {
                            picker.$refs.minTimePicker.selectableRange = []
                            isUpdate = true
                        }
                    }
                }
            }
            if (typeof this.minDateUnwatch === "function") {
                this.minDateUnwatch()
            }
            if (typeof this.maxDateUnwatch === "function") {
                this.maxDateUnwatch()
            }
            this.minDateUnwatch = picker.$watch("minDate", callback)
            this.maxDateUnwatch = picker.$watch("maxDate", callback)
        },
        formatDateTime (picker) {
            const dateTable = this.getDateTableVm(picker.$children)
            typeof this.dateTimeCallBack === "function" && this.$off("input", this.dateTimeCallBack)
            typeof this.onDateTimePick === "function" && dateTable && dateTable.$off("pick", this.onDateTimePick)
            let isUpdate = true
            this.dateTimeCallBack = (val) => {
                if (!val) return
                if (this.getIsCurrentDay(val)) {
                    if (isUpdate) {
                        picker.$refs.timepicker.selectableRange = getDisabledTime(this.disabled, new Date())
                        isUpdate = false
                    }
                } else {
                    if (Array.isArray(picker.$refs.timepicker.selectableRange) && picker.$refs.timepicker.selectableRange.length !== 0) {
                        picker.$refs.timepicker.selectableRange = []
                        isUpdate = true
                    }
                }
            }
            this.onDateTimePick = (val) => {
                setTimeout(() => {
                    if (!val) return
                    if (this.disabled === "before") {
                        if (this.getIsCurrentDay(val)) {
                            this.getDatePickerInstance().emitInput(new Date())
                        } else {
                            this.getDatePickerInstance().emitInput(modifyWithTimeString(val, typeof this.defaultTime === "string" ? this.defaultTime : "00:00:00"))
                        }
                    } else {
                        this.getDatePickerInstance().emitInput(modifyWithTimeString(val, typeof this.defaultTime === "string" ? this.defaultTime : "00:00:00"))
                    }
                }, 50)
            }
            dateTable && dateTable.$on("pick", this.onDateTimePick)
            this.dateTimeCallBack(this.value)
            this.$on("input", this.dateTimeCallBack)
        },
        getIsCurrentDay (val) {
            if (isDateObject(val)) return formatDate(val) === formatDate(new Date())
            return formatDate(parseDate(val, this.realValueFormat)) === formatDate(new Date())
        },
        getDateTableVm (children) {
            const allChildren = children || this.$children
            let ch = null
            if (allChildren.length) {
                for (const child of allChildren) {
                    if (child.$options._componentTag === "date-table") {
                        ch = child
                        break
                    } else {
                        ch = this.getDateTableVm(child.$children)
                        if (ch) break
                    }
                }
            }
            return ch
        }
    },
    beforeDestroy () {
        typeof this.minDateUnwatch === "function" && this.minDateUnwatch()
        typeof this.maxDateUnwatch === "function" && this.maxDateUnwatch()
        typeof this.dateTimeCallBack === "function" && this.$off("input", this.dateTimeCallBack)
    }
}
</script>
